import React, { lazy, Suspense, useState, useEffect, useRef } from 'react';
import { FileText, ExternalLink, Check, Info } from 'lucide-react';
import { Article } from '../../types';
import { Translations } from '../../i18n/translations';
import { parseArticle, prefetchArticle } from '../../services/articleParserService';

// Modal is rendered only after the user clicks a card; keep it out of the
// per-card bundle so the article grid is lighter on initial paint.
const ArticleModal = lazy(() => import('./ArticleModal').then(m => ({ default: m.ArticleModal })));

interface ArticleCardProps {
  article: Article;
  size: 'small' | 'medium' | 'large';
  deClickbaitMode: boolean;
  cleanMode: boolean;
  onMarkAsRead: () => void;
  t: Translations;
  articleParsingEnabled?: boolean;
}

// In cleanMode, swap a clickbait headline for the AI-generated cleaned
// version. Falls back to the original if no cleaned variant is available.
const getDisplayTitle = (article: Article, cleanMode: boolean): string => {
  if (cleanMode && article.isClickbait && article.cleanedTitle) {
    return article.cleanedTitle;
  }
  return article.title;
};

const getDisplayDescription = (article: Article, cleanMode: boolean): string => {
  if (cleanMode && article.isClickbait && article.cleanedDescription) {
    return article.cleanedDescription;
  }
  return article.description;
};

const isEmptyDescription = (description: string): boolean => {
  const trimmed = description.trim().toLowerCase();
  return !trimmed || trimmed === 'comments' || trimmed === 'comment';
};

const categoryMap: Record<string, keyof Translations> = {
  'Curiosity Gap': 'clickbaitCategoryCuriosityGap',
  'Vague Drama': 'clickbaitCategoryVagueDrama',
  'Info Withheld': 'clickbaitCategoryInfoWithheld',
  'Emotional': 'clickbaitCategoryEmotional',
  'Superlatives': 'clickbaitCategorySuperlatives',
  'Fear Bait': 'clickbaitCategoryFearBait',
  'Teaser': 'clickbaitCategoryTeaser',
  'Metaphor': 'clickbaitCategoryMetaphor',
};

const explanationMap: Record<string, keyof Translations> = {
  'Curiosity Gap': 'clickbaitExplainCuriosityGap',
  'Vague Drama': 'clickbaitExplainVagueDrama',
  'Info Withheld': 'clickbaitExplainInfoWithheld',
  'Emotional': 'clickbaitExplainEmotional',
  'Superlatives': 'clickbaitExplainSuperlatives',
  'Fear Bait': 'clickbaitExplainFearBait',
  'Teaser': 'clickbaitExplainTeaser',
  'Metaphor': 'clickbaitExplainMetaphor',
};

const getTranslatedCategory = (category: string | undefined, t: Translations): string | undefined => {
  if (!category) return undefined;
  const translationKey = categoryMap[category];
  return translationKey ? (t[translationKey] as string) : category;
};

const getTranslatedExplanation = (category: string | undefined, t: Translations): string | undefined => {
  if (!category) return undefined;
  const translationKey = explanationMap[category];
  return translationKey ? (t[translationKey] as string) : undefined;
};

export const ArticleCard: React.FC<ArticleCardProps> = ({
  article,
  size,
  deClickbaitMode,
  cleanMode,
  onMarkAsRead,
  t,
  articleParsingEnabled = true,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [lazyExcerpt, setLazyExcerpt] = useState<string | null>(null);
  const [loadingExcerpt, setLoadingExcerpt] = useState(false);
  const [excerptLoadAttempted, setExcerptLoadAttempted] = useState(false);
  const [showClickbaitTooltip, setShowClickbaitTooltip] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        setShowClickbaitTooltip(false);
      }
    };

    if (showClickbaitTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showClickbaitTooltip]);

  const displayTitle = getDisplayTitle(article, cleanMode);
  const displayDescription = getDisplayDescription(article, cleanMode);
  // Whether cleanMode actually swapped anything in. False when the AI
  // didn't return a cleaned variant, OR when the language-mismatch guard
  // in the AI service fell back to the original. Used to keep the
  // "CLEANSED" badge honest — it should only appear when the visible text
  // really was rewritten.
  const cleanseApplied = cleanMode &&
    article.isClickbait &&
    !!article.cleanedTitle &&
    article.cleanedTitle !== article.title;
  const articleUrl = article.link;
  const isLarge = size === 'large';
  const isSmall = size === 'small';
  const hasDescription = !isEmptyDescription(displayDescription);

  // Use lazy-loaded excerpt if we don't have a description
  const effectiveDescription = hasDescription ? displayDescription : (lazyExcerpt || '');
  const hasEffectiveDescription = !!effectiveDescription;

  const descriptionLines = effectiveDescription.split('\n').filter(p => p.trim());
  const previewLineCount = isLarge ? 4 : isSmall ? 2 : 3;
  const charLimit = isLarge ? 400 : isSmall ? 150 : 250;

  const showClickbaitBadge = deClickbaitMode && article.isClickbait;

  // Lazy load excerpt for articles without descriptions (only attempt once)
  useEffect(() => {
    if (!hasDescription && articleParsingEnabled && !lazyExcerpt && !loadingExcerpt && !excerptLoadAttempted) {
      setLoadingExcerpt(true);
      setExcerptLoadAttempted(true);
      parseArticle(article.link).then(result => {
        if (result?.excerpt) {
          const truncated = result.excerpt.length > 150
            ? result.excerpt.substring(0, 150).replace(/\s+\S*$/, '') + '...'
            : result.excerpt;
          setLazyExcerpt(truncated);
        }
        setLoadingExcerpt(false);
      }).catch(() => {
        setLoadingExcerpt(false);
      });
    }
  }, [hasDescription, articleParsingEnabled, lazyExcerpt, loadingExcerpt, excerptLoadAttempted, article.link]);

  // Build preview text with character limit
  let previewText = '';
  let previewLines: string[] = [];

  for (let i = 0; i < Math.min(descriptionLines.length, previewLineCount); i++) {
    const line = descriptionLines[i];
    if (previewText.length + line.length > charLimit) {
      const remaining = charLimit - previewText.length;
      if (remaining > 20) {
        const truncatedLine = line.substring(0, remaining).replace(/\s+\S*$/, '') + '...';
        previewLines.push(truncatedLine);
      }
      break;
    }
    previewText += line;
    previewLines.push(line);
  }

  const handleTitleClick = (e: React.MouseEvent) => {
    if (articleParsingEnabled) {
      e.preventDefault();
      setShowModal(true);
    }
  };

  // Predictively warm the parse cache the moment the user signals intent
  // by hovering or pressing the card. By the time they click the title
  // and the modal mounts, parseArticle() is usually already resolved.
  // Cheap because the service de-dupes in-flight calls and short-circuits
  // on cache hits.
  const handlePrefetch = (): void => {
    if (articleParsingEnabled) prefetchArticle(article.link);
  };

  return (
    <>
      <article
        className="bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 p-4 hover:shadow-lg transition-shadow"
        onMouseEnter={handlePrefetch}
        onFocus={handlePrefetch}
        onTouchStart={handlePrefetch}
      >
        {/* Header: Source • Time • Badge - all on one compact line */}
        <div className="flex items-center gap-2 text-xs mb-2 flex-wrap">
          <span className="font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
            {article.feedName}
          </span>
          <span className="text-gray-400 dark:text-gray-500">•</span>
          <span className="text-gray-500 dark:text-gray-400">
            {article.pubDate.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
          </span>
          {showClickbaitBadge && (
            <>
              <span className="text-gray-400 dark:text-gray-500">•</span>
              {cleanseApplied ? (
                <span className="px-1.5 py-0.5 font-semibold uppercase text-[10px] bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                  {t.cleaned}
                </span>
              ) : (
                <div className="relative inline-flex items-center" ref={tooltipRef}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowClickbaitTooltip(!showClickbaitTooltip);
                    }}
                    className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-800"
                    aria-label="Why is this clickbait?"
                  >
                    <span className="font-semibold uppercase">{getTranslatedCategory(article.clickbaitCategory, t) || t.clickbait}</span>
                    {(article.clickbaitCategory || article.clickbaitExplanation) && <Info className="w-3 h-3" />}
                  </button>
                  {showClickbaitTooltip && (article.clickbaitCategory || article.clickbaitExplanation) && (
                    <>
                      {/* Mobile: centered modal */}
                      <div className="sm:hidden fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/50" onClick={() => setShowClickbaitTooltip(false)}>
                        <div className="bg-black text-white rounded-lg shadow-xl border border-gray-600 p-4 text-sm max-w-xs" onClick={(e) => e.stopPropagation()}>
                          {article.clickbaitExplanation || getTranslatedExplanation(article.clickbaitCategory, t)}
                        </div>
                      </div>
                      {/* Desktop: anchored tooltip */}
                      <div className="hidden sm:block absolute left-0 top-full mt-1 z-50 w-56 p-2.5 text-xs bg-black text-white rounded shadow-xl border border-gray-600">
                        <div className="text-white">
                          {article.clickbaitExplanation || getTranslatedExplanation(article.clickbaitCategory, t)}
                        </div>
                        <div className="absolute -top-1 left-4 w-2 h-2 bg-black rotate-45 border-l border-t border-gray-600"></div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Title */}
        <h3 className={`font-bold font-serif leading-tight mb-2 text-gray-900 dark:text-white ${
          isLarge ? 'text-2xl' : isSmall ? 'text-base' : 'text-xl'
        }`}>
          <a
            href={articleUrl}
            onClick={handleTitleClick}
            target={articleParsingEnabled ? undefined : "_blank"}
            rel={articleParsingEnabled ? undefined : "noopener noreferrer"}
            className="hover:underline cursor-pointer"
            aria-label={articleParsingEnabled
              ? `${displayTitle} - ${t.readFullArticle}`
              : `${displayTitle} (opens in new tab)`
            }
          >
            {displayTitle}
          </a>
        </h3>

        {/* Description */}
        {hasEffectiveDescription && (
          <div className={`text-gray-700 dark:text-gray-300 leading-relaxed mb-3 ${
            isLarge ? 'text-sm' : 'text-xs'
          }`}>
            {previewLines.map((para, i) => (
              <p key={i} className={i < previewLines.length - 1 ? 'mb-1' : ''}>{para}</p>
            ))}
          </div>
        )}

        {/* Loading indicator */}
        {!hasDescription && loadingExcerpt && (
          <div className="text-gray-400 dark:text-gray-500 text-xs mb-3 italic">
            {t.loadingArticle}
          </div>
        )}

        {/* Footer: Compact action row */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            {articleParsingEnabled && (
              <button
                onClick={() => setShowModal(true)}
                className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                aria-label={`${t.readFullArticle}: ${displayTitle}`}
              >
                <FileText className="w-3 h-3" aria-hidden="true" />
                <span className="hidden sm:inline">{t.readFullArticle}</span>
                <span className="sm:hidden">{t.readShort}</span>
              </button>
            )}
            <a
              href={articleUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-gray-500 dark:text-gray-400 hover:underline flex items-center gap-1"
              aria-label={`${t.visitOriginal}: ${displayTitle} (opens in new tab)`}
            >
              <ExternalLink className="w-3 h-3" aria-hidden="true" />
              <span className="hidden sm:inline">{t.visitOriginal}</span>
              <span className="sm:hidden">{t.linkShort}</span>
            </a>
          </div>
          <button
            onClick={onMarkAsRead}
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 flex items-center gap-1"
            aria-label={`${t.markAsRead}: ${displayTitle}`}
          >
            <Check className="w-3 h-3" aria-hidden="true" />
            <span className="hidden sm:inline">{t.markAsRead}</span>
            <span className="sm:hidden">{t.doneShort}</span>
          </button>
        </div>
      </article>

      {showModal && (
        <Suspense fallback={null}>
          <ArticleModal
            article={article}
            displayTitle={displayTitle}
            displayDescription={displayDescription}
            articleUrl={articleUrl}
            onClose={() => setShowModal(false)}
            onMarkAsRead={onMarkAsRead}
            t={t}
            articleParsingEnabled={articleParsingEnabled}
          />
        </Suspense>
      )}
    </>
  );
};
